import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  api,
  computePoints,
  formatDuration,
  statusBar,
  statusClass,
  statusFromPoints,
  LIMIT_POINTS,
} from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Square, Save } from "lucide-react";
import { toast } from "sonner";
import { getSelectedWorkerId } from "@/lib/workerStore";
import StatusBadge from "@/components/app/StatusBadge";

function TimerHeader({ worker, tool }) {
  return (
    <Card className="p-4 border-slate-200">
      <p className="text-xs uppercase tracking-wider text-slate-500">Employee</p>
      <p className="font-medium text-slate-900">{worker.name}</p>
      <div className="mt-3 pt-3 border-t border-slate-100">
        <p className="text-xs uppercase tracking-wider text-slate-500">Tool</p>
        <p className="font-medium text-slate-900">
          <span className="font-mono">{tool.code}</span> · {tool.name}
        </p>
        <p className="text-xs text-slate-500 mt-1 font-mono">
          {tool.vibration_m_s2.toFixed(1)} m/s²
        </p>
      </div>
    </Card>
  );
}

function TimerDisplay({ running, elapsedS, points, status }) {
  const pct = Math.min(100, (points / LIMIT_POINTS) * 100);
  const isStopped = status === "stop";
  return (
    <Card
      className={`p-6 border-2 text-center ${statusClass(status)} ${
        isStopped ? "timer-pulse-stop border-red-500" : "border-slate-200"
      }`}
      data-testid="timer-display"
    >
      <p className="text-xs font-bold uppercase tracking-[0.2em]">
        {running ? "Trigger time running" : "Stopped — review & save"}
      </p>
      <p
        className="font-mono font-black text-6xl mt-3 text-slate-900"
        data-testid="timer-elapsed"
      >
        {formatDuration(elapsedS)}
      </p>
      <div className="mt-5">
        <p className="text-xs uppercase tracking-wider text-slate-700">
          HSE points
        </p>
        <p
          className="font-display font-black text-5xl text-slate-900"
          data-testid="timer-points"
        >
          {points.toFixed(1)}
        </p>
      </div>
      <div className="mt-4">
        <div className="h-3 bg-white/60 rounded-full overflow-hidden border border-white">
          <div
            className={`h-full ${statusBar(status)}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider mt-1 text-slate-700">
          <span>0</span>
          <span>100 action</span>
          <span>400 limit</span>
        </div>
      </div>
      <div className="mt-4">
        <StatusBadge status={status} />
      </div>
    </Card>
  );
}

const TICK_INTERVAL_MS = 250;

export default function Timer() {
  const { toolCode } = useParams();
  const navigate = useNavigate();
  const [tool, setTool] = useState(null);
  const [worker, setWorker] = useState(null);
  const [running, setRunning] = useState(true);
  const [startedAt] = useState(() => new Date());
  const [endedAt, setEndedAt] = useState(null);
  const [elapsedS, setElapsedS] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const tickRef = useRef(null);

  const loadContext = useCallback(async () => {
    const wid = getSelectedWorkerId();
    if (!wid) {
      navigate("/worker", { replace: true });
      return;
    }
    try {
      const [toolRes, workersRes] = await Promise.all([
        api.get(`/tools/by-code/${encodeURIComponent(toolCode)}`),
        api.get("/workers"),
      ]);
      const w = workersRes.data.find((x) => x.id === wid);
      if (!w) {
        toast.error("Selected employee no longer exists");
        navigate("/worker", { replace: true });
        return;
      }
      setTool(toolRes.data);
      setWorker(w);
    } catch (err) {
      console.error("Timer context load failed:", err);
      toast.error("Tool not found");
      navigate("/worker", { replace: true });
    }
  }, [toolCode, navigate]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  useEffect(() => {
    if (!running) return undefined;
    tickRef.current = setInterval(() => {
      setElapsedS(Math.floor((Date.now() - startedAt.getTime()) / 1000));
    }, TICK_INTERVAL_MS);
    return () => clearInterval(tickRef.current);
  }, [running, startedAt]);

  const points = tool ? computePoints(tool.vibration_m_s2, elapsedS) : 0;
  const status = statusFromPoints(points);

  const stop = () => {
    setRunning(false);
    setEndedAt(new Date());
    if (tickRef.current) clearInterval(tickRef.current);
  };

  const save = async () => {
    if (!worker || !tool) return;
    setSaving(true);
    try {
      const end = endedAt || new Date();
      await api.post("/exposures", {
        worker_id: worker.id,
        tool_code: tool.code,
        start_time: startedAt.toISOString(),
        end_time: end.toISOString(),
        notes,
      });
      toast.success("Exposure saved");
      navigate("/worker", { replace: true });
    } catch (err) {
      console.error("Exposure save failed:", err);
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!tool || !worker) {
    return <p className="text-slate-500">Loading…</p>;
  }

  return (
    <div className="space-y-5" data-testid="timer-page">
      <TimerHeader worker={worker} tool={tool} />
      <TimerDisplay
        running={running}
        elapsedS={elapsedS}
        points={points}
        status={status}
      />

      {running ? (
        <Button
          onClick={stop}
          className="w-full touch-btn h-16 bg-red-600 hover:bg-red-700 text-white text-lg font-bold rounded-xl gap-3 active:scale-[0.98]"
          data-testid="timer-stop-btn"
        >
          <Square className="h-6 w-6" /> Stop timer
        </Button>
      ) : (
        <>
          <Card className="p-4 border-slate-200">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">
              Notes (optional)
            </p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Job, location, comments…"
              data-testid="timer-notes-input"
            />
          </Card>
          <Button
            onClick={save}
            disabled={saving}
            className="w-full touch-btn h-16 bg-slate-900 hover:bg-slate-800 text-white text-lg font-bold rounded-xl gap-3"
            data-testid="timer-save-btn"
          >
            <Save className="h-6 w-6" /> {saving ? "Saving…" : "Save exposure"}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/worker")}
            className="w-full touch-btn h-14 border-2 border-slate-300 text-slate-700 rounded-xl"
            data-testid="timer-cancel-btn"
          >
            Discard
          </Button>
        </>
      )}
    </div>
  );
}
